<script setup lang="ts">
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
</script>

<template>
  <div class="content-container about-view">
    <h1>{{ t('about.title') }}</h1>

    <!-- 制作風景セクション -->
    <section class="dev-scene">
      <h2>{{ t('about.dev_scene.title') }}</h2>
      <div class="image-container">
        <img src="@/assets/dev_scene.webp" alt="Development Scene" class="dev-image">
      </div>
      <p class="image-caption">{{ t('about.dev_scene.caption') }}</p>
    </section>

    <!-- ご挨拶 -->
    <section>
      <h2>{{ t('about.greeting.title') }}</h2>
      <p style="white-space: pre-wrap;">{{ t('about.greeting.content') }}</p>
    </section>

    <!-- サイトのコンセプト -->
    <section>
      <h2>{{ t('about.concept.title') }}</h2>
      <p style="white-space: pre-wrap;">{{ t('about.concept.content') }}</p>
    </section>

    <!-- 運営者プロフィール -->
    <section>
      <h2>{{ t('about.profile.title') }}</h2>
      <p>{{ t('about.profile.content1') }}</p>
      <p>{{ t('about.profile.content2') }}</p>
      <p>
        <a href="https://www.youtube.com/@sora_sakurai_jp" target="_blank" rel="noopener noreferrer"
          class="youtube-link">
          <span class="icon">📺</span> {{ t('about.profile.link_text') }}
        </a>
      </p>
    </section>

    <!-- 技術的なこだわり -->
    <section>
      <h2>{{ t('about.technical.title') }}</h2>
      <p>{{ t('about.technical.stack') }}</p>
      <ul class="stack-list">
        <li>{{ t('about.technical.backend') }}</li>
        <li>{{ t('about.technical.frontend') }}</li>
        <li>{{ t('about.technical.tech') }}</li>
      </ul>
      <p>{{ t('about.technical.content') }}</p>
    </section>

    <!-- 今後の展望 -->
    <section>
      <h2>{{ t('about.outlook.title') }}</h2>
      <p>{{ t('about.outlook.content') }}</p>
    </section>

    <!-- 開発メモ -->
    <section>
      <h2>{{ t('tech_blog.title') }}</h2>
      <p>
        <router-link to="/tech/image-processing" class="tech-link">
          📝 {{ t('tech_blog.image_processing.title') }}
        </router-link>
      </p>
      <p>
        <router-link to="/tech/unsplash-integration" class="tech-link">
          📝 {{ t('tech_blog.unsplash_integration.title') }}
        </router-link>
      </p>
    </section>

    <!-- お問い合わせ -->
    <section>
      <h2>{{ t('about.contact.title') }}</h2>
      <p>{{ t('about.contact.content') }}</p>
      <div class="contact-btn-container">
        <router-link to="/contact" class="contact-link">{{ t('footer.contact') }}</router-link>
      </div>
    </section>

    <div class="footer-links">
      <router-link to="/" class="back-link">{{ t('common.back') }}</router-link>
    </div>
  </div>
</template>

<style scoped>
@import '@/assets/text-page.css';

.about-view {
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
}

h1::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #42b983, #17a2b8);
  border-radius: 2px;
}

section {
  margin-bottom: 4rem;
}

h2 {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  border-left: 5px solid #17a2b8;
  padding-left: 1rem;
}

p {
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 1rem;
}

.image-container {
  margin: 2rem 0;
  display: flex;
  justify-content: center;
  width: 100%;
}

.dev-image {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
}

.image-caption {
  text-align: center;
  font-size: 0.9rem;
  color: #718096;
  margin-top: 1rem;
  font-style: italic;
}

.youtube-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #e53e3e;
  text-decoration: none;
  font-weight: bold;
  transition: color 0.2s;
}

.youtube-link:hover {
  color: #c53030;
  text-decoration: underline;
}

.stack-list {
  margin: 1.5rem 0;
  padding-left: 1.5rem;
  list-style: none;
}

.stack-list li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
}

.stack-list li::before {
  content: '🚀';
  position: absolute;
  left: 0;
}

.contact-btn-container {
  margin-top: 2rem;
  text-align: center;
}

.contact-link {
  display: inline-block;
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: bold;
  box-shadow: 0 4px 15px rgba(118, 75, 162, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;
}

.contact-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(118, 75, 162, 0.4);
}

.footer-links {
  margin-top: 5rem;
  text-align: center;
  border-top: 1px solid #e2e8f0;
  padding-top: 2rem;
}

.back-link {
  color: #718096;
  text-decoration: none;
  font-size: 1rem;
  transition: color 0.2s;
}

.back-link:hover {
  color: #2c3e50;
}

.tech-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #42b983;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.tech-link:hover {
  text-decoration: underline;
  color: #3aa876;
}
</style>
